import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { keys, useMe, useOffline, useTrees } from '../../lib/queries';
import { Modal } from '../../components/Modal';
import { PullToRefresh } from '../../components/PullToRefresh';
import { useT } from '../../i18n';
import { WhatsNew } from './WhatsNew';

export function TreeList() {
  const t = useT();
  const trees = useTrees();
  const me = useMe();
  const offline = useOffline((o) => o.offline);
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api<{ id: string }>('POST', '/trees', { name: name.trim() }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: keys.trees });
      void nav({ to: '/t/$treeId', params: { treeId: r.id } });
    },
  });
  const initial = (me.data?.user.displayName ?? user?.displayName ?? me.data?.user.email ?? '?').trim().charAt(0).toUpperCase() || '?';
  const verified = me.data?.user.emailVerified ?? true;
  const refresh = useCallback(() => Promise.all([qc.refetchQueries({ queryKey: keys.trees }), qc.refetchQueries({ queryKey: keys.me })]), [qc]);
  return (
    <PullToRefresh onRefresh={refresh}>
      <div className="page">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 24 }}>
          <span className="wordmark">Lineage</span>
          <Link to="/settings" className="avatar avatar-link" aria-label={t.trees.settings} title={t.trees.settings}>{initial}</Link>
        </div>
        {!verified && (
          <div className="banner" style={{ marginBottom: 16 }}>
            {t.trees.verifyEmail(me.data?.user.email ?? '')}
          </div>
        )}
        {offline && (
          <div role="status" className="banner" style={{ marginBottom: 16 }}>
            {t.trees.offline}
          </div>
        )}
        <WhatsNew />
        <h1 style={{ marginBottom: 16 }}>{t.trees.yourTrees}</h1>
        <div className="stack">
          {trees.isLoading && <p className="muted">{t.common.loading}</p>}
          {trees.isError && !trees.data && (
            <div className="stack" style={{ gap: 8 }}>
              <p className="muted">{t.trees.couldntLoad}</p>
              <button className="btn" type="button" onClick={() => void trees.refetch()}>{t.common.tryAgain}</button>
            </div>
          )}
          {trees.data?.map((tr) => (
            <Link key={tr.id} className="list-item" to="/t/$treeId" params={{ treeId: tr.id }}>
              <div className="grow">
                <h3 style={{ fontSize: 24 }}>{tr.name}</h3>
                <span className="muted">{t.trees.people(tr.peopleCount)}</span>
              </div>
              <span className="pill">{t.common.roles[tr.role] ?? tr.role}</span>
            </Link>
          ))}
          {trees.data?.length === 0 && <p className="muted">{t.trees.none}</p>}
          <button className="btn btn-primary" type="button" onClick={() => setCreating(true)}>{t.trees.start}</button>
        </div>
        {creating && (
          <Modal title={t.trees.start} onClose={() => setCreating(false)}>
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) create.mutate();
              }}
            >
              <label className="field">
                <span>{t.trees.treeName}</span>
                <input className="input" autoFocus value={name} maxLength={80} placeholder={t.trees.treeNameExample} onChange={(e) => setName(e.target.value)} />
              </label>
              {create.error && <div className="error">{(create.error as Error).message}</div>}
              <button className="btn btn-primary" disabled={!name.trim() || create.isPending}>{t.trees.create}</button>
            </form>
          </Modal>
        )}
      </div>
    </PullToRefresh>
  );
}
